import style from '@/less/main.less';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Formik } from 'formik';
import React from 'react';
import { toast } from 'react-semantic-toasts';
import { Button, Form, FormField, FormGroup, Icon, Modal } from 'semantic-ui-react';
import { number, object, string } from 'yup';
import FilterDropdown from '../FilterDropdown';
import { FormTextInput } from '../Form/FormTextInput';
import Bulletin from '../Bulletin';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InviteUserModal = ({ isOpen, onClose }: InviteUserModalProps) => {
  const { positionStore, temporaryEmployeeStore } = useStores();

  return (
    <Modal
      className={style.modal}
      open={isOpen}
      size="tiny"
      closeIcon={<Icon name="close" size="tiny" color="black" />}
      closeOnDimmerClick={false}
      onClose={onClose}
    >
      <Formik
        validationSchema={object().shape({
          email: string().email('Please enter a valid email address.').required().label('Email'),
          firstName: string().required().label('First Name'),
          lastName: string().required().label('Last Name'),
          positionId: number().required().min(1, 'Position is required').label('Position'),
        })}
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
          positionId: 0,
        }}
        onSubmit={async ({ email, firstName, lastName, positionId }, { setFieldError }) => {
          try {
            await temporaryEmployeeStore.inviteUser(email, firstName, lastName, positionId);

            onClose();

            toast({
              type: 'success',
              icon: 'check circle',
              title: 'User Invited',
              description: 'The user has been invited to the SOL Employee app.',
              time: 5000,
            });
          } catch (error) {
            // @ts-ignore
            if (error.message && error.message.includes('already exists')) {
              setFieldError('email', 'This email has already been invited to onboarding.');
            }
          }
        }}
      >
        {({ values, handleBlur, setFieldValue, handleSubmit }) => {
          return (
            <>
              <Modal.Header>Invite New User</Modal.Header>

              <Modal.Content>
                <Bulletin
                  errorMessage={temporaryEmployeeStore.errorMessage}
                  isLoading={temporaryEmployeeStore.isLoading}
                  handleDismiss={() => {
                    temporaryEmployeeStore.clearError();
                  }}
                />

                <Modal.Description style={{ marginBottom: 18 }}>
                  Invite a new user to the SOL Employee app. They&apos;ll receive an email to
                  download the mobile app and will be guided through a simple onboarding process.
                </Modal.Description>

                <Form>
                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="email">Email</label>
                      <FormTextInput
                        id="email"
                        value={values.email}
                        placeholder="john.doe@example.com"
                        onChange={(_, data) => setFieldValue('email', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="email"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>

                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="firstName">First Name</label>
                      <FormTextInput
                        id="firstName"
                        value={values.firstName}
                        placeholder="John"
                        onChange={(_, data) => setFieldValue('firstName', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="firstName"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>

                    <FormField width={16}>
                      <label htmlFor="lastName">Last Name</label>
                      <FormTextInput
                        id="lastName"
                        value={values.lastName}
                        placeholder="Doe"
                        onChange={(_, data) => setFieldValue('lastName', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="lastName"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>

                  <FormField>
                    <label htmlFor="positionId">Position</label>
                    <FilterDropdown
                      name="positionId"
                      placeholder="Select a position"
                      value={values.positionId}
                      options={positionStore.values.map(({ id, description }) => ({
                        text: description,
                        value: id,
                      }))}
                      multiple={false}
                      changeDropdown={(_, value) => {
                        setFieldValue('positionId', value);
                      }}
                    />
                    <ErrorMessage
                      name="positionId"
                      render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                    />
                  </FormField>
                </Form>
              </Modal.Content>

              <Modal.Actions>
                <Button color="grey" basic style={{ fontSize: '0.9rem' }} onClick={onClose}>
                  Cancel
                </Button>
                <Button color="green" style={{ fontSize: '0.9rem' }} onClick={() => handleSubmit()}>
                  <FontAwesomeIcon icon={['fas', 'envelope']} style={{ marginRight: 8 }} />
                  Send Invite Email
                </Button>
              </Modal.Actions>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};
